export const mapGender = (gender?: string): string => {
  if (gender === "M") return "Male";
  if (gender === "F") return "Female";
  return "";
};

export const allowOnlyNumbers = (value: string) => {
  return value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
};

export const removeSpecialCharacters = (value: string): string => {
  return value.replace(/[^A-Za-z0-9\s]/g, "");
};

export const removeFullSpecialCharacters = (value: string): string => {
  return value.replace(/[^A-Za-z\s]/g, "");
};

export const formatUploadedDateTime = (dateString: string) => {
  if (!dateString) return { date: "", time: "" };

  const date = new Date(dateString);

  const formattedDate = date.toISOString().split("T")[0];

  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return {
    date: formattedDate,
    time: formattedTime,
  };
};

export const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12;

  return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
};

export const getFileViewUrl = (filePath?: string) => {
  const baseUrl = process.env.REACT_APP_DOCUMENT_BASE_URL;
  if (!filePath || !baseUrl) return "";
  return `${baseUrl.replace(/\/$/, "")}/${filePath.replace(/^\//, "")}`;
};

export const formatFileSizeFromKB = (sizeInKB?: number | string): string => {
  if (!sizeInKB) return "0 KB";

  const size = Number(sizeInKB);
  if (isNaN(size)) return "0 KB";

  if (size >= 1024 * 1024) {
    const gb = size / (1024 * 1024);
    return `${Number(gb.toFixed(2))} GB`;
  }

  if (size >= 1024) {
    const mb = size / 1024;
    return `${Number(mb.toFixed(2))} MB`;
  }

  return `${size} KB`;
};

export const maskMobileNumber = (mobile?: string) => {
  if (!mobile) return "";

  if (mobile.length <= 4) return mobile;

  const lastFour = mobile.slice(-4);
  return `******${lastFour}`;
};

export const maskEmail = (email?: string) => {
  if (!email) return "";

  const [name, domain] = email.split("@");
  if (!domain) return email;

  if (name.length <= 1) return `*@${domain}`;

  const firstChar = name.charAt(0);
  const maskedPart = "*".repeat(Math.max(name.length - 1, 4));

  return `${firstChar}${maskedPart}@${domain}`;
};

export const formatOtpTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

export const maskWhatsAppNumber = (number?: string) => {
  if (!number) return "";

  if (number.length <= 4) return number;

  const lastFour = number.slice(-4);
  return `******${lastFour}`;
};

export const maskItsNumber = (its?: string) => {
  if (!its) return "";

  if (its.length <= 4) return its;

  const lastFour = its.slice(-4);
  return `******${lastFour}`;
};

export const maskLoginId = (loginId?: string) => {
  if (!loginId) return "";

  if (loginId.includes("@")) {
    return maskEmail(loginId);
  }

  return maskItsNumber(loginId);
};

export const validatePhoneNumberInput = (value: string) => {
  // Allow only digits, +, (, ) and -
  let sanitized = value.replace(/[^\d+()-]/g, "");

  // + only at first position
  if (sanitized.includes("+")) {
    sanitized = "+" + sanitized.replace(/\+/g, "").replace(/^/, "");
  }

  // Limit length
  return sanitized.slice(0, 20);
};

export const normalizeDate = (date?: string | null) => {
  const value = date?.trim();
  if (!value || value === "0000-00-00") {
    return "";
  }

  // HTML date inputs only accept YYYY-MM-DD. OCR commonly returns DD-MM-YYYY.
  const dayFirst = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dayFirst) {
    const [, day, month, year] = dayFirst;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  const yearFirst = value.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (yearFirst) {
    const [, year, month, day] = yearFirst;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return "";
};

// export const getPassportTypeName = (typeCode?: string): string => {
//   switch (typeCode?.toUpperCase()) {
//     case "P":
//       return "Personal / Ordinary";
//     case "D":
//       return "Diplomatic";
//     case "S":
//       return "Service / Official";
//     default:
//       return "";
//   }
// };

export const getPassportTypeName = (typeCode?: string): string => {
  switch (typeCode?.toUpperCase()) {
    // Ordinary Passport
    case "P": // Backward compatibility
    case "PP":
      return "Personal / Ordinary";

    // Diplomatic Passport
    case "D": // Backward compatibility
    case "PD":
      return "Diplomatic";

    // Official / Service Passport
    case "S": // Backward compatibility
    case "PO":
      return "Service / Official";

    // Emergency Passport
    case "PE":
      return "Emergency Passport";

    // Refugee Travel Document
    case "PR":
      return "Refugee Travel Document";

    // Stateless Passport
    case "PS":
      return "Stateless Passport";

    // Alien Passport
    case "PT":
      return "Alien Passport";

    // Laissez-Passer
    case "PL":
      return "Laissez-Passer";

    // Military Passport
    case "PM":
      return "Military Passport";

    default:
      return "";
  }
};

export const FormatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  //06 DEC 2023
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

export const FormatDisplayDate = (date: string | Date) => {
  const d = new Date(date);

  return `${d.getDate()} ${d.toLocaleString("en-US", {
    month: "short",
  })}, ${d.getFullYear()}`;
};

export const toSentenceCase = (value: string) => {
  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^\w/, (char) => char.toUpperCase());
};